import React from "react";
import {
  Typography,
  Box,
  Container,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Gavel as GavelIcon,
  NavigateNext as BulletIcon,
} from "@mui/icons-material";

import SeoMetaTags from "../components/SeoMetaTags";

// Styled components
const PageHeader = styled(Box)(({ theme }) => {
  // Get the theme URL dynamically
  const themeUrl = window.wpData?.themeUrl || '';
  
  return {
    backgroundImage: `linear-gradient(rgba(14, 49, 88, 0.85), rgba(14, 49, 88, 0.85)), url('${themeUrl}/build/grey-building.jpg')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    color: 'white',
    padding: theme.spacing(10, 0, 6, 0),
    textAlign: 'center',
    marginBottom: theme.spacing(6),
    marginTop: theme.spacing(2),
  };
});

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2.5rem',
  marginBottom: theme.spacing(2),
}));



const PageContainer = styled(Container)(({ theme }) => ({
  paddingBottom: theme.spacing(8),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "2rem",
  marginBottom: theme.spacing(3),
  color: "#003876", // Blue brand color
  position: "relative",
  paddingBottom: theme.spacing(1.5),
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "80px",
    height: "3px",
    backgroundColor: "#d2232a", // Red brand color
  },
}));

const TermCard = styled(Card)(({ theme }) => ({
  height: "100%",
  marginBottom: theme.spacing(3),
  borderRadius: "8px",
  boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 6px 15px rgba(0,0,0,0.1)",
  },
}));

const TermTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.25rem",
  marginBottom: theme.spacing(1),
  color: "#003876", // Blue brand color
  display: "flex",
  alignItems: "center",
}));

const TermEmphasis = styled("span")(({ theme }) => ({
  fontWeight: 700,
  fontStyle: "italic",
}));

// Glossary term data
const glossaryTerms = [
  {
    term: "Plaintiff / Defendant",
    definition: "In a civil lawsuit, the plaintiff is the person or entity who initiates the lawsuit (the one who is suing), and the defendant is the person or entity being sued or accused of some wrongdoing. In criminal law, the equivalent of a plaintiff is the Crown or prosecution (since crimes are prosecuted by the state), and the defendant is the accused person."
  },
  {
    term: "Affidavit",
    definition: "A written statement of facts that the author swears or affirms to be true, usually in front of a notary or commissioner of oaths. Affidavits are used as evidence in court proceedings. For example, in a divorce or civil case, you might submit an affidavit to provide your testimony or supporting information in written form."
  },
  {
    term: "Power of Attorney (POA)",
    definition: "A legal document that gives someone else (the \"attorney\" or agent) the authority to act on your behalf in financial or legal matters. A Power of Attorney is often used so that if you become incapacitated or are unavailable, a trusted person can manage your affairs. (It's important to note this does not make them an actual lawyer – \"attorney\" in this context is just the term for the designated decision-maker.)"
  },
  {
    term: "Probate",
    definition: "The legal process of validating a deceased person's will and administering their estate. Probate typically occurs in the Surrogate Court (a division of the Alberta Court of King's Bench) and involves confirming the executor's authority, inventorying assets, paying off debts, and distributing the remaining assets to beneficiaries. Obtaining a Grant of Probate gives the executor the legal power to deal with the estate assets."
  },
  {
    term: "Retainer",
    definition: "In law, a retainer can refer to two things: (1) an upfront fee paid to a lawyer to secure their services and cover initial work, often placed into a trust account; or (2) the agreement between a lawyer and client outlining the scope of work and terms of the relationship (often called a retainer agreement or engagement letter). When you \"put a lawyer on retainer,\" you are essentially hiring them and often paying a deposit for future services."
  },
  {
    term: "Statement of Claim",
    definition: "A legal document that starts a civil lawsuit in Alberta's Court of King's Bench. The Statement of Claim sets out the plaintiff's allegations against the defendant and the remedy or relief the plaintiff is seeking. Once a Statement of Claim is filed and served, the defendant must respond (typically by filing a Statement of Defence) within a set time, or risk default judgment."
  },
  {
    term: "Mediation",
    definition: "An alternative dispute resolution process in which a neutral third-party (the mediator) helps the conflicting parties try to reach a voluntary, negotiated settlement. Mediation is confidential and generally non-binding – the mediator does not impose a decision, but rather facilitates discussion. If the parties reach an agreement, they can formalize it in a settlement. This process is common in family law and civil disputes as a way to resolve issues without going to trial."
  },
  {
    term: "Solicitor-Client Privilege",
    definition: "A fundamental principle that communications between you and your lawyer are confidential and cannot be disclosed without your permission. This encourages clients to be open and honest with their lawyers, knowing that information shared for the purpose of obtaining legal advice will be protected. (There are very limited exceptions, such as if a client is using legal advice to commit a crime, but otherwise this privacy is strictly upheld.)"
  }
];

// Format the definition text to make emphasized text italic and bold
const formatDefinition = (text) => {
  // Split by asterisks
  const parts = text.split(/(\*\*|\*)/g);
  let isItalic = false;
  let isBold = false;
  
  return parts.map((part, index) => {
    if (part === "**") {
      isBold = !isBold;
      return null;
    } else if (part === "*") {
      isItalic = !isItalic;
      return null;
    } else {
      if (isBold && isItalic) {
        return <TermEmphasis key={index} style={{ fontWeight: 700, fontStyle: 'italic' }}>{part}</TermEmphasis>;
      } else if (isBold) {
        return <TermEmphasis key={index} style={{ fontWeight: 700 }}>{part}</TermEmphasis>;
      } else if (isItalic) {
        return <TermEmphasis key={index} style={{ fontStyle: 'italic' }}>{part}</TermEmphasis>;
      } else {
        return part;
      }
    }
  });
};

function Glossary() {
  return (
    <>
      <SeoMetaTags
        title="Legal Glossary - Common Legal Terms Explained | Sentry Law"
        description="Understanding important legal terminology in plain language. Our glossary explains common legal terms to help you better understand your case."
        slug="glossary"
      />
      {/* Header Section */}
      <PageHeader>
        <Container maxWidth="lg">
          <PageTitle variant="h1">
            Glossary of Common Legal Terms
          </PageTitle>
          <Typography variant="subtitle1" sx={{ maxWidth: 900, mx: 'auto', mb: 3, lineHeight: 1.7 }}>
            Understanding important legal terminology in plain language.
          </Typography>
        </Container>
      </PageHeader>
    
      <PageContainer maxWidth="lg">
        {/* Introduction */}
        <Paper elevation={1} sx={{ p: 4, mb: 5, borderRadius: 2 }}>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
            Legal language can be confusing. Our glossary explains some common legal terms in plain English to help you better understand your case and communications from your lawyer. Here are a few terms you might encounter:
          </Typography>
        </Paper>
        
        {/* Glossary Terms */}
        <Grid container spacing={3}>
          {glossaryTerms.map((item, index) => (
            <Grid item xs={12} key={index}>
              <TermCard>
                <CardContent sx={{ p: 3 }}>
                  <TermTitle variant="h3">
                    <GavelIcon sx={{ mr: 1, color: "#d2232a" }} fontSize="small" />
                    {item.term}
                  </TermTitle>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {formatDefinition(item.definition)}
                  </Typography>
                </CardContent>
              </TermCard>
            </Grid>
          ))}
        </Grid>
        
        {/* Note */}
        <Paper elevation={1} sx={{ p: 3, mt: 4, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic', lineHeight: 1.6 }}>
            Our full glossary contains many more terms. If you come across an unfamiliar word or acronym, check here or ask us – we're always happy to explain any legal jargon in simpler terms.
          </Typography>
        </Paper>
      </PageContainer>
    </>
  );
}

export default Glossary;